import { useState } from "react";
import { toast } from "sonner";
import { Send, Mail, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { SectionHeader } from "./SectionHeader";
import { postContact } from "@/lib/api";

const INTENTS = ["Collaboration", "Interview / Podcast", "Research inquiry", "Other"];

export const Contact = ({ settings }) => {
  const [form, setForm] = useState({ name: "", email: "", intent: "Collaboration", message: "" });
  const [loading, setLoading] = useState(false);
  const email = settings?.contactEmail;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      const res = await postContact(form);
      toast.success(res.message || "Message sent.");
      setForm({ name: "", email: "", intent: "Collaboration", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try the direct email instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 md:py-36" data-testid="contact-section">
      <div className="section-shell grid grid-cols-1 gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHeader index="05" overline="Reach the Field" title="Collaborate" />
          <Reveal>
            <p className="font-sans text-base md:text-lg font-light leading-relaxed text-slate-400">
              For research collaboration, interviews, or inquiry partnership — send a note, or
              write directly. Every message reaches the Resonant Spark Project.
            </p>
            {email && (
              <a
                href={`mailto:${email}`}
                className="btn-gold mt-8"
                data-testid="contact-email-cta"
              >
                <Mail className="h-3.5 w-3.5" strokeWidth={1.5} /> {email}
              </a>
            )}
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form onSubmit={submit} className="glass-card rounded-xl p-8 md:p-10" data-testid="contact-form">
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                <div>
                  <label className="overline mb-2 block">Name</label>
                  <input
                    value={form.name}
                    onChange={update("name")}
                    data-testid="contact-name"
                    className="w-full rounded-none border-0 border-b border-slate-700 bg-transparent px-0 py-2 font-sans text-white outline-none transition-colors focus:border-plasma-teal"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="overline mb-2 block">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    data-testid="contact-email"
                    className="w-full rounded-none border-0 border-b border-slate-700 bg-transparent px-0 py-2 font-sans text-white outline-none transition-colors focus:border-plasma-teal"
                    placeholder="you@domain.com"
                  />
                </div>
              </div>

              <div className="mt-8">
                <label className="overline mb-2 block">Intent</label>
                <select
                  value={form.intent}
                  onChange={update("intent")}
                  data-testid="contact-intent"
                  className="w-full rounded-none border-0 border-b border-slate-700 bg-transparent px-0 py-2 font-sans text-white outline-none transition-colors focus:border-plasma-teal [&>option]:bg-[#0B0D17]"
                >
                  {INTENTS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>

              <div className="mt-8">
                <label className="overline mb-2 block">Message</label>
                <textarea
                  value={form.message}
                  onChange={update("message")}
                  rows={4}
                  data-testid="contact-message"
                  className="w-full resize-none rounded-none border-0 border-b border-slate-700 bg-transparent px-0 py-2 font-sans text-white outline-none transition-colors focus:border-plasma-teal"
                  placeholder="Tell me about your inquiry…"
                />
              </div>

              <button type="submit" disabled={loading} className="btn-plasma mt-10 disabled:opacity-60" data-testid="contact-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" strokeWidth={1.5} />}
                {loading ? "Sending…" : "Send message"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
